/* =============================================================================
   SLOAT collection + search results
   - Filters preview the result count live ("Show 4 products") and apply on tap.
   - Sort, filter chips and Clear swap results in place. URL stays shareable,
     Back and Forward work.
   - Load more appends the next page; optional auto-load near the end.
   - Same-document View Transition on swaps where supported, crossfade elsewhere.
   Depends on window.Sloat from sloat-core.js.
   ========================================================================== */
const S = window.Sloat;
const { $, $$ } = S;
const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

class Results {
  constructor(section) {
    this.section = section;
    this.id = section.dataset.sectionId;
    this.cache = new Map();
    this.bind();
    this.observeMore();
  }

  get dialogName() {
    return `filter-${this.id}`;
  }

  url(params) {
    const u = new URL(location.pathname, location.origin);
    params.forEach((v, k) => {
      if (v !== '' && v !== null) u.searchParams.append(k, v);
    });
    return u;
  }

  formParams() {
    const form = $(`[data-sloat-dialog="${this.dialogName}"] [data-filter-form]`) || $('[data-filter-form]', this.section);
    const params = new URLSearchParams();
    if (!form) return params;
    new FormData(form).forEach((v, k) => {
      if (String(v).trim() !== '') params.append(k, v);
    });
    return params;
  }

  async load(url) {
    const key = url.toString();
    if (!this.cache.has(key)) {
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.id);
      this.cache.set(
        key,
        fetch(fetchUrl).then((r) => {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
      );
    }
    try {
      return parse(await this.cache.get(key));
    } catch (e) {
      this.cache.delete(key);
      throw e;
    }
  }

  bind() {
    const dialog = $(`[data-sloat-dialog="${this.dialogName}"]`);

    // Live preview of the count while choosing filters.
    if (dialog) {
      dialog.addEventListener('change', () => this.preview());
      dialog.addEventListener('input', (e) => {
        if (e.target.type === 'number') {
          clearTimeout(this.priceTimer);
          this.priceTimer = setTimeout(() => this.preview(), 400);
        }
      });
      dialog.addEventListener('click', (e) => {
        const apply = e.target.closest('[data-filter-apply]');
        if (apply) {
          e.preventDefault();
          const url = this.url(this.formParams());
          this.load(url).catch(() => {});
          S.dialogs.close(dialog).then(() => this.apply(url));
          return;
        }
        const clear = e.target.closest('[data-filter-clear]');
        if (clear) {
          e.preventDefault();
          $$('[data-filter-form] input', dialog).forEach((i) => {
            if (i.type === 'checkbox') i.checked = false;
            if (i.type === 'number') i.value = '';
          });
          this.preview();
        }
      });
      dialog.addEventListener('sloat:close', () => this.syncForm());
    }

    this.section.addEventListener('click', (e) => {
      const link = e.target.closest('[data-filter-link], [data-sort], [data-load-more]');
      if (!link || link.closest('[data-sloat-dialog^="filter-"]')) return;
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      if (link.matches('[data-load-more]')) return this.more(link);
      const url = new URL(link.href, location.origin);
      if (link.matches('[data-sort]')) {
        $$('[data-sort-menu][open]', this.section).forEach((d) => (d.open = false));
        const sheet = link.closest('dialog');
        if (sheet) {
          this.load(url).catch(() => {});
          S.dialogs.close(sheet).then(() => this.apply(url));
          return;
        }
      }
      this.apply(url);
    });

    // Sort links in the collection header (outside this section) drive these results too.
    if (!Results.headerBound) {
      Results.headerBound = true;
      document.addEventListener('click', (e) => {
        const link = e.target.closest('.sloat-colhead [data-sort]');
        if (!link || e.metaKey || e.ctrlKey) return;
        const target = $$('[data-results]').map((el) => el._results).find(Boolean);
        if (!target) return;
        e.preventDefault();
        link.closest('[data-sort-menu]')?.removeAttribute('open');
        const u = new URL(location.href);
        u.searchParams.set('sort_by', link.dataset.sort);
        u.searchParams.delete('page');
        target.apply(u);
      });
    }

    // Close sort popovers when clicking elsewhere.
    document.addEventListener('click', (e) => {
      $$('[data-sort-menu][open]').forEach((d) => {
        if (!d.contains(e.target)) d.open = false;
      });
    });

    window.addEventListener('popstate', (e) => {
      if (e.state && e.state.sloatResults === this.id) this.apply(new URL(location.href), { push: false });
    });
    history.replaceState({ ...(history.state || {}), sloatResults: this.id }, '');
  }

  async preview() {
    const url = this.url(this.formParams());
    const label = $(`[data-sloat-dialog="${this.dialogName}"] [data-filter-apply-label]`);
    const button = label && label.closest('button');
    button && (button.dataset.state = 'loading');
    try {
      const html = await this.load(url);
      const count = Number($('[data-results]', html)?.dataset.resultsCount || 0);
      const styles = this.section && this.section.dataset.resultsUnit === 'styles';
      const [one, many] = styles ? ['style', 'styles'] : ['product', 'products'];
      if (label) label.textContent = count === 0 ? `No ${many}` : `Show ${count} ${count === 1 ? one : many}`;
      if (button) button.disabled = count === 0;
    } catch (e) {
      if (label) label.textContent = 'Show results';
    } finally {
      if (button) delete button.dataset.state;
    }
  }

  async apply(url, { push = true } = {}) {
    this.section.classList.add('is-loading');
    try {
      const html = await this.load(url);
      const fresh = $('[data-results]', html);
      if (!fresh) throw new Error('No results section');
      const swap = () => {
        const body = $('[data-results-body]', this.section);
        const freshBody = $('[data-results-body]', fresh);
        // Dialogs inside the body are open-state sensitive; swap only closed ones.
        body.replaceWith(freshBody);
        const freshCount = $('[data-results-count-label]', fresh);
        const n = Number(fresh.dataset.resultsCount || 0);
        $$('[data-results-count-label]').forEach((label) => {
          label.textContent = freshCount ? freshCount.textContent : `${n} ${n === 1 ? 'product' : 'products'}`;
        });
        this.section.dataset.resultsCount = fresh.dataset.resultsCount;
        const tb = $('[data-toolbar]', fresh);
        const active = tb?.dataset.activeCount || '0';
        $$('[data-filter-badge]').forEach((b) => { b.dataset.count = active; b.textContent = active; });
        if (tb?.dataset.sortLabel) $$('[data-sort-current]').forEach((el) => (el.textContent = tb.dataset.sortLabel));
        if (tb?.dataset.sortLabel) $$('.sloat-colhead [data-sort]').forEach((a) => a.classList.toggle('is-current', a.textContent.trim() === tb.dataset.sortLabel));
        $$('[data-reveal]', freshBody).forEach((el) => el.classList.add('is-in', 'is-instant'));
        S.media(freshBody);
        this.observeMore();
      };
      if (document.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.section.classList.remove('is-loading');
        await document.startViewTransition(swap).finished.catch(() => {});
      } else {
        swap();
      }
      if (push) history.pushState({ sloatResults: this.id }, '', url.pathname + url.search);
      S.header && S.header.onScroll && S.header.onScroll(true);
      const toolbar = $('[data-toolbar]', this.section);
      if (toolbar && toolbar.getBoundingClientRect().top < 0) {
        const y = toolbar.getBoundingClientRect().top + scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sloat-header-visible')) || 0) - 8;
        scrollTo({ top: y, behavior: 'auto' });
      }
    } catch (e) {
      location.href = url.toString();
    } finally {
      this.section.classList.remove('is-loading');
    }
  }

  syncForm() {
    // After the drawer closes without applying, reset inputs to what is live.
    const live = new URL(location.href).searchParams;
    const dialog = $(`[data-sloat-dialog="${this.dialogName}"]`);
    if (!dialog) return;
    $$('[data-filter-form] input', dialog).forEach((i) => {
      if (i.type === 'checkbox') i.checked = live.getAll(i.name).includes(i.value);
      if (i.type === 'number') i.value = live.get(i.name) || '';
    });
  }

  async more(link) {
    if (link.dataset.state === 'loading') return;
    link.dataset.state = 'loading';
    try {
      const html = await this.load(new URL(link.href, location.origin));
      const grid = $('[data-results-grid]', this.section);
      const items = $$('[data-results-grid] > li', html).filter((li) => !li.matches('.sloat-tile'));
      items.forEach((li, i) => {
        li.classList.add('is-appended');
        li.style.setProperty('--i', String(Math.min(i, 8)));
        li.removeAttribute('data-reveal');
        grid.appendChild(li);
      });
      S.media(grid);
      const nextWrap = $('[data-load-more-wrap]', html);
      const wrap = $('[data-load-more-wrap]', this.section);
      if (nextWrap) wrap.replaceWith(nextWrap);
      else wrap.remove();
      this.observeMore();
    } catch (e) {
      location.href = link.href;
    } finally {
      delete link.dataset.state;
    }
  }

  observeMore() {
    if (this.section.dataset.autoload !== 'true' || !('IntersectionObserver' in window)) return;
    this.moreObserver?.disconnect();
    const link = $('[data-load-more]', this.section);
    if (!link) return;
    this.moreObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          this.moreObserver.disconnect();
          this.more(link);
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.moreObserver.observe(link);
  }
}

/* -----------------------------------------------------------------------------
   Per-color cards (collection grids with "Show each color as its own card")
   - The card already stands for one color, so a dot click opens the product page
     in that color instead of re-coloring the card. Runs in the capture phase,
     ahead of the card handler in sloat-core.js.
   - Hovering a dot previews that color (image swap in sloat-core.js) and its name.
   -------------------------------------------------------------------------- */
const ColorCards = {
  init() {
    window.addEventListener(
      'click',
      (e) => {
        const sw = e.target.closest('[data-per-color] [data-card-swatch]');
        if (!sw) return;
        e.preventDefault();
        e.stopPropagation();
        const card = sw.closest('[data-card]');
        const color = sw.dataset.cardSwatch;
        const d = S.cards && S.cards.data(card);
        const u = new URL(card.dataset.url, location.origin);
        if (d) {
          const inColor = d.variants.filter((v) => v[1] === color);
          const first = inColor.find((v) => v[3]) || inColor[0];
          if (first) u.searchParams.set('variant', first[0]);
        }
        location.href = u.pathname + u.search;
      },
      true
    );
    document.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse') return;
      const sw = e.target.closest('[data-per-color] [data-card-swatch]');
      if (sw) this.label(sw.closest('[data-card]'), sw.dataset.cardSwatch);
    });
    document.addEventListener('pointerout', (e) => {
      if (e.pointerType !== 'mouse') return;
      const list = e.target.closest('[data-per-color] .sloat-card__swatches');
      if (list && !list.contains(e.relatedTarget)) {
        const card = list.closest('[data-card]');
        this.label(card, card.dataset.color);
      }
    });
  },
  label(card, color) {
    const el = card && $('[data-card-color]', card);
    if (el && color) el.textContent = color;
  },
};
ColorCards.init();

const init = () => $$('[data-results]').forEach((el) => !el._results && (el._results = new Results(el)));
if (S && S.ready) init();
else document.addEventListener('sloat:ready', init, { once: true });
document.addEventListener('shopify:section:load', init);
